import { memo, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useReactiveVar } from '@apollo/client';

import { updateCartItemCount, usePersistentCartItems } from 'store/cartItems';
import { productFlatListVar, sidesPlanDetailVar } from 'store/productsFlatList';
import { useGetSideDetailQuery } from 'commonModules/orderFlow';

import NewDishDisplay from '@components/NewDishDisplay';
import CuminButton from '@components/Button';
import {
  getDecimalPricePerMeal,
  getSidesRecomendation,
  getSidesRequired,
} from '@utils/helpers';
import { getCategorizedProductsType } from '@utils/types';
import { useElevar } from '@utils/elevar';

import { useStyles } from './useStyles';

interface ISuggestedSides {
  categories: getCategorizedProductsType[];
  setSideCounts: (count: number) => void;
  mode?: string;
  countryCode?: string;
}

const SuggestedSides = ({
  categories,
  setSideCounts,
  mode,
  countryCode,
}: ISuggestedSides) => {
  const [sideRecommended, setSidesRecommended] = useState<any>({});

  const classes = useStyles();

  const {
    elevarAddToCartEvent,
    elevarRemoveFromCartEvent,
    elevarViewItemEvent,
  } = useElevar();

  const sideDetail = useGetSideDetailQuery({
    countryCode,
    isSkipped: mode === 'edit',
  });
  if (sideDetail) {
    sidesPlanDetailVar(sideDetail);
  }
  const cartItems = usePersistentCartItems();
  const { productFlatList } = useReactiveVar(productFlatListVar);

  useEffect(() => {
    const requiredSides = getSidesRequired({
      cartItems,
      productFlatList,
    });

    const recommendedSides: { [id: string]: number } = getSidesRecomendation({
      requiredSides,
      categories,
    });

    const numberOfsideAdded = Object.keys(recommendedSides)?.reduce(
      (sum, currentValue) => sum + (cartItems[+currentValue] || 0),
      0
    );

    setSideCounts(numberOfsideAdded || 0);

    setSidesRecommended(recommendedSides);
  }, [cartItems, productFlatList]);

  const handleAddAll = () => {
    Object.keys(sideRecommended)?.map((items: string) => {
      const itemAdded = cartItems[+items] || 0;
      const itemToAdd = sideRecommended[+items] || 0;

      if (productFlatList[+items]?.isActive && itemAdded < itemToAdd) {
        const amountToAdd = itemToAdd;
        updateCartItemCount(productFlatList[+items]?.id, amountToAdd);
      }
    });

    if (!mode && elevarAddToCartEvent) {
      Object.entries(sideRecommended).map(([productId, quantity]: any) => {
        const product = productFlatList[productId];

        for (let i = 1; i <= +quantity; i++) {
          //fire elevar add to cart event
          elevarAddToCartEvent(product);
        }
      });
    }

    // for GA event
    //@ts-ignore
    window?.dataLayer.push({
      event: 'recommended_sides_add',
    });
  };

  return (
    <Box className={classes.mainContainer}>
      <Typography
        variant="body2"
        align="center"
        color="cinnamon.main"
        m="0 auto 24px"
        maxWidth={{ xs: '270px', sm: '345px' }}
      >
        {mode === 'edit'
          ? 'Your selected sides have been added! You can add more pairings for your meals.'
          : sideDetail?.price
          ? `Add sides for ${
              sideDetail?.currency_symbol +
              getDecimalPricePerMeal(sideDetail?.price).toFixed(2)
            } each!`
          : ''}
      </Typography>
      <Box className={classes.bottomContainer}>
        <CuminButton
          color="turmeric"
          variant="contained"
          borderRadius="24"
          height="48"
          textColor="garamMasala.main"
          className={classes.addBtn}
          onClick={handleAddAll}
        >
          Add All Recommended Sides
        </CuminButton>
      </Box>
      <Box className={classes.itemContainer}>
        {Object.keys(sideRecommended)
          ?.sort((a, b) => {
            if (sideRecommended[b] === 0) {
              return -1;
            }
            return 0;
          })
          ?.map((items: string) => {
            if (productFlatList[+items]?.isActive) {
              return (
                <NewDishDisplay
                  product={productFlatList[+items]}
                  valueRecommended={sideRecommended[+items]}
                  key={items}
                  isSuggestion={true}
                  isInfoDisplay={true}
                  mode={mode}
                  elevarAddToCartEvent={elevarAddToCartEvent}
                  elevarRemoveFromCartEvent={elevarRemoveFromCartEvent}
                  elevarViewItemEvent={elevarViewItemEvent}
                />
              );
            }
          })}
      </Box>
    </Box>
  );
};

export default memo(SuggestedSides);
